import { useMutationWrapper } from 'apis/config/use-api-wrapper';
import type { AxiosError } from 'axios';
import { UseMutationOptions, useQueryClient } from 'react-query';
import http from '../config/http-service';
import type { RestaurantInquiry, UpdateRestaurantInquiryStatusPayload } from './types';
import { restaurantInquiriesEndpoint } from './use-create-restaurant-inquiry-api';

// PATCH api/v1/cms/restaurant-inquiries/:id/status — the only route that moves
// an inquiry through its lifecycle. 400 if the current status is terminal
// (FULFILLED/UNFULFILLED/CANCELLED). Side effect: transitioning into PROCESSING
// with assignedTo=null makes the caller the permanent assignee (sticky) — the
// response reflects the new assignee, so refetching covers it.
type TData = RestaurantInquiry;
type TError = AxiosError;
type TVariables = { id: number | string } & UpdateRestaurantInquiryStatusPayload;

type Options = Omit<UseMutationOptions<TData, TError, TVariables>, 'mutationFn'>;
type TReturnType = ReturnType<typeof useMutationWrapper<TData, TError, TVariables>>;

export function useUpdateRestaurantInquiryStatusApi(options?: Options): TReturnType {
  const queryClient = useQueryClient();
  const { onSuccess: userOnSuccess, ...restOptions } = options || {};

  const mutationFn = async ({ id, status, reason }: TVariables): Promise<TData> => {
    // Only include the reason when set, so unrelated transitions don't send
    // reason: undefined.
    const body: Record<string, unknown> = { status };
    if (reason !== undefined) body.reason = reason;
    const response = await http.patch(`${restaurantInquiriesEndpoint}/${id}/status`, body);
    return response.data?.data ?? response.data;
  };

  return useMutationWrapper(mutationFn, {
    mutationKey: [restaurantInquiriesEndpoint, 'update-status'],
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries([restaurantInquiriesEndpoint]);
      userOnSuccess?.(data, variables, context);
    },
    ...restOptions,
  });
}
